import React from 'react'
import {Card, Col,  Row} from 'react-bootstrap'
import {useLocation} from 'react-router'
import {trans} from 'sharedKernel/i18n'
import {BreakWordParagraph, ScreenContainer} from './style'
import {Loading} from 'components/loading'
import {NoContent} from 'components/NoContent'
import {
    AttachmentsArea,
    AttachmentsItem,
    AttachmentsTitle,
    CoverImage,
    DescriptionText,
    DescriptionTitle
} from 'sharedKernel/styledComponents/visualizationComponents/styles'
import {isUserOwner} from './behavior'
import {VisualizationEventScreenProps} from './contract'
import {EditEventButton} from './buttons/EditEventButton'
import {RemoveEventButton} from './buttons/RemoveEventButton'
import {LaunchEventButton} from './buttons/LaunchEventButton'
import DOMPurify from "dompurify"
import { ReactI18NextChild } from "react-i18next"


export const VisualizationScreen = ({
                                        isFetching,
                                        data,
                                        isFileFetching,
                                        fileData
                                    }: VisualizationEventScreenProps): JSX.Element => {
    const filesEvent = {
        eventFiles: fileData?.filter((file: { type: string }) => file.type === 'supportFiles'),
        homePageSlider: fileData?.find((file: { type: string }) => file.type === 'homePageSlider'),
        coverImage: fileData?.find((file: { type: string }) => file.type === 'coverImage')
    }
    const {state}: any = useLocation()

    const today = new Date()

    const labels = {
        notFound: trans('Não encontrado'),
        generalInformation: trans('Informações Gerais'),
        description: trans('Descrição'),
        attachments: trans('Anexos'),
        supportFilesNotFound: trans('Nenhum arquivo de apoio foi incluído neste evento'),
        closed: trans('Encerrado')
    }

    if (isFetching ?? false) {
        return (
            <Loading/>
        )
    }
    if (data === undefined) {
        return <NoContent text={labels.notFound}/>
    }

    const ownerId = data.data.systemUserId
    const isOwner = isUserOwner(ownerId)

    const eventId = data.id
    let isLaunched = data.data.isLaunched
    if (state && typeof state.setToLaunched === 'boolean') {
        isLaunched = state.setToLaunched
    }

    const getIdeaImage = (url?: string): string => {
        const fallBackIdeaImage = require('../../../../assets/images/Imagem_do_evento.png')

        return url != null && url.length > 0 ? url : fallBackIdeaImage
    }

    const isAvailable = (): string => {
        if (!data.data.isLaunched) {
            return 'Pendente'
        }
        const endDateString = data.data.endDate?.split('T')[0]
        const endTimeString = data.data.endTime
        let endDateTime: Date
        if (endDateString !== null && endTimeString !== null) {
            endDateTime = new Date(`${endDateString}T${endTimeString}`)
        } else {
            endDateTime = new Date(endDateString)
        }
        const isEnded = endDateTime < today

        if (isEnded) {
            return 'Encerrado'
        }

        return 'Disponível'
    }

    const formatDateString = (dataInicio: Date | null = null, dataFim: Date | null = null): string => {
        if (dataInicio == null && dataFim == null) {
            return ''
        }

        const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

        if (dataInicio && !dataFim) {
            return `${dataInicio.getDate()} de ${meses[dataInicio.getMonth()]} ${dataInicio.getFullYear()}`
        }

        if (dataInicio && dataFim) {
            const dataInicioFormatada = `${dataInicio.getDate()} de ${meses[dataInicio.getMonth()]} ${dataInicio.getFullYear()}`
            const dataFimFormatada = `${dataFim.getDate()} de ${meses[dataFim.getMonth()]} ${dataFim.getFullYear()}`

            return dataInicioFormatada !== dataFimFormatada ? `${dataInicioFormatada} - ${dataFimFormatada}` : dataInicioFormatada
        }

        return ''
    }

    const formatAddress = (): string => {
        if (data.data.isInPerson === true) {
            if (data.data.address.id !== null && data.data.address.addressLineOne !== '') {
                return `${data.data.address.addressLineOne}, ${data.data.address.addressNumber} - ${data.data.address.addressLineTwo}, ${data.data.address.addressNeighborhood}, ${data.data.address.addressCity} - ${data.data.address.addressState}, ${data.data.address.addressZipCode}`
            }
        } else {
            return 'Online'
        }
        return ''
    }

    const checkUrl = (answer: string): boolean => {
        try {
            new URL(answer)
            return true
        } catch (err) {
            return false
        }
    }

    function getHeader() {
        return <>
            <div style={{display: 'flex', gap: '16px'}}>
                <div style={{flex: 1, paddingLeft: 10,  paddingBottom:  30}}>
                        <h2><b>{data.data.name}</b></h2>
                </div>
                <div style={{flexShrink: 0, textAlign: 'right', display: 'flex',  gap: '16'}}>
                    {!isLaunched ? (
                        <LaunchEventButton eventId={eventId} isOwner={isOwner} alreadyLaunched={isLaunched}/>
                    ) : (<></>)
                    }
                    <EditEventButton  eventId={data.id} isOwner={isOwner} alreadyLaunched={isLaunched}/>
                    <RemoveEventButton eventId={data.id} isOwner={isOwner} alreadyLaunched={isLaunched}/>
                </div>
            </div>            
            <CoverImage backgroundImg={getIdeaImage(filesEvent?.homePageSlider?.path)}/>
        </>;
    }

    function getGeneralPanel() {
        return <Row>
            <Col sm={12} className="gy-0">
                <DescriptionTitle>{labels.generalInformation}</DescriptionTitle>
            </Col>

            <Col sm={12} className="gy-0">
                <BreakWordParagraph><b>{data.data.isPaid === true ? 'Evento Pago' : 'Evento Gratuito'}</b></BreakWordParagraph>
            </Col>
            <Col sm={12}>
                <BreakWordParagraph><b>Status: </b>{isAvailable()}</BreakWordParagraph>
            </Col>
            <Col sm={12} className="gy-0">
                <BreakWordParagraph><b>Data: </b>{formatDateString(data.data.startDate !== null && data.data.startDate !== '' ? new Date(data.data.startDate) : null, data.data.endDate !== null && data.data.endDate !== '' ? new Date(data.data.endDate) : null)}
                </BreakWordParagraph>
            </Col>
            <Col sm={12} className="gy-0">
                <BreakWordParagraph><b>Localização: </b>{formatAddress()}</BreakWordParagraph>
            </Col>
            {data.data.website !== '' && (
                <Col sm={12} className="gy-3">
                    <BreakWordParagraph><b>Site oficial do evento: </b>
                        {checkUrl(data.data.website) ? (
                            <a
                                href={data.data.website}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {data.data.website}
                            </a>
                        ) : (
                            data.data.website
                        )}
                    </BreakWordParagraph>
                </Col>
            )}
            {filesEvent.eventFiles !== null && filesEvent.eventFiles !== undefined && filesEvent.eventFiles.length > 0 ? (
                <Col sm={12} className="gy-3">
                    <AttachmentsArea>
                        <AttachmentsTitle>{labels.attachments}:</AttachmentsTitle>
                        {!isFileFetching ? (
                            filesEvent.eventFiles.map((file: {
                                path: string | undefined;
                                name: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | Iterable<ReactI18NextChild> | null | undefined
                            }, index: any) => (
                                <AttachmentsItem key={`attachments-item-${index}`}>
                                    <a key={`item-${index}`} href={file.path} target="_blank"
                                       download={file.name}>
                                        {file.name}
                                    </a>
                                </AttachmentsItem>
                            ))
                        ) : (
                            <AttachmentsItem>{labels.supportFilesNotFound}</AttachmentsItem>
                        )}
                    </AttachmentsArea>
                </Col>
            ) : null}
        </Row>
    }

    function getClearText(htmlText: string): string {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlText;
        return (tempDiv.textContent || tempDiv.innerText || '').trim();
    }
    
    function hasTicketInformation(data: any): boolean {
        const description =     getClearText(data?.data?.ticketDescription);

        if (!description) return false;

        return description.trim().length > 0;
    }

    function getDetailPanel(labels: {
        notFound: string;
        description: string;
        attachments: string;
        supportFilesNotFound: string;
        closed: string
    }, data: { data: { description: string | Node; ticketDescription: string | Node } }) {
        return <Row>
            <Col sm={12}>
                <DescriptionTitle>{labels.description}</DescriptionTitle>
                <hr/>
                <DescriptionText>
                    <div
                        dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(data.data.description)}}
                    />
                    {hasTicketInformation(data)  && (
                        <>
                            <div>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(data.data.ticketDescription)
                                    }}
                                />
                            </div>
                        </>
                    )}

        </DescriptionText>
    </Col>
    </Row>
    }


    return <ScreenContainer>
        {getHeader()}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <Card style={{  width: '380px', flexShrink: 1}}>
                <Card.Body>
                    {getGeneralPanel()}
                </Card.Body>
            </Card>
            <Card style={{flex: 1, backgroundColor: 'transparent', border: '0px  none' }}>
                <Card.Body>
                    {getDetailPanel(labels, data)}
                </Card.Body>
            </Card>
        </div>
    </ScreenContainer>
}
